import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { 
  Auth, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  sendPasswordResetEmail,
  OAuthProvider, 
  UserCredential 
} from '@angular/fire/auth';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  AuthResponse, 
  LoginRequest, 
  TokenValidationRequest, 
  TokenValidationResponse 
} from '../models/auth.model';
import { AccountSummary } from '../models/account.model';

type AuthState = {
  account: AccountSummary | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class AuthSignalService {
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private publicUrl = `${environment.apiPublicUrl}/auth`;  // Solo para login/logout
  private baseUrl = `${environment.apiUrl}/auth`;      // Para otros endpoints si es necesario

  // State
  private state = signal<AuthState>({
    account: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null
  });

  // Selectors
  readonly account = computed(() => this.state().account);
  readonly token = computed(() => this.state().token);
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly isLoading = computed(() => this.state().loading);
  readonly error = computed(() => this.state().error);

  // Sources
  login$ = new Subject<LoginRequest>();
  loginWithMicrosoft$ = new Subject<void>();
  logout$ = new Subject<void>();
  forgotPassword$ = new Subject<{email: string}>();
  checkAuthState$ = new Subject<void>();

  constructor() {
    console.log('AuthSignalService initialized');
    
    // Inicializar estado desde localStorage
    this.checkAuthState();

    // Configurar reducers
    this.login$.pipe(
      tap(() => {
        this.state.update(state => ({ ...state, loading: true, error: null }));
      }),
      switchMap(request => this.loginApi(request)),
      takeUntilDestroyed()
    ).subscribe({
      next: (response) => {
        this.handleAuthSuccess(response);
      },
      error: (error) => {
        this.handleAuthError(error);
      }
    });

    this.loginWithMicrosoft$.pipe(
      tap(() => {
        this.state.update(state => ({ ...state, loading: true, error: null }));
      }),
      switchMap(() => this.loginWithMicrosoftApi()),
      takeUntilDestroyed()
    ).subscribe({
      next: (response) => {
        this.handleAuthSuccess(response);
      },
      error: (error) => {
        this.handleAuthError(error);
      }
    });

    this.logout$.pipe(
      tap(() => {
        this.state.update(state => ({ ...state, loading: true }));
      }),
      switchMap(() => this.logoutApi()),
      takeUntilDestroyed()
    ).subscribe({
      next: () => {
        this.clearAuthData();
        this.snackBar.open('Sesión cerrada correctamente', 'Cerrar', { duration: 3000 });
        this.router.navigate(['/auth/login']);
      },
      error: (error) => {
        console.error('Error logout:', error);
        this.clearAuthData();
        this.router.navigate(['/auth/login']);
      }
    });

    this.forgotPassword$.pipe(
      tap(() => {
        this.state.update(state => ({ ...state, loading: true, error: null }));
      }),
      switchMap(request => this.forgotPasswordApi(request.email)),
      takeUntilDestroyed()
    ).subscribe({
      next: () => {
        this.state.update(state => ({ ...state, loading: false }));
        this.snackBar.open(
          'Se ha enviado un correo para restablecer tu contraseña', 
          'Cerrar', 
          { duration: 5000 }
        );
      },
      error: (error) => {
        let errorMessage = 'Error al enviar el correo de recuperación';
        if (error.code === 'auth/user-not-found') {
          errorMessage = 'No existe una cuenta con este correo electrónico';
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'El correo electrónico no es válido';
        }
        
        this.state.update(state => ({ 
          ...state, 
          loading: false, 
          error: errorMessage 
        }));
        
        this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
      }
    });

    this.checkAuthState$.pipe(
      takeUntilDestroyed()
    ).subscribe(() => {
      this.checkAuthState();
    });
  }

  /**
   * Check authentication state - auth_user from localStorage, auth_token from cookies
   */
  checkAuthState(): void {
    console.log('Checking auth state - Signal Service');
    // Solo leer auth_user de localStorage (contiene roles/permisos)
    const userJson = localStorage.getItem('auth_user');
    
    let account = null;
    if (userJson) {
      try {
        account = JSON.parse(userJson);
      } catch (e) {
        console.error('Error parsing user data', e);
      }
    }
    
    if (account) {
      // Asumir autenticado si hay auth_user
      // Si token expiró, se detectará en la primera petición real
      this.state.update(state => ({
        ...state,
        isAuthenticated: true,
        token: 'cookie-based',
        account
      }));
      
      // Redirección si es necesario
      if (this.router.url.startsWith('/auth')) {
        this.router.navigate(['/dashboard']);
      }
    } else {
      this.clearAuthData();
    }
  }

  /**
   * Login with email and password using Firebase Authentication
   */
  private loginApi(request: LoginRequest): Observable<AuthResponse> {
    console.log('Attempting login with email:', request.email);
    return from(
      signInWithEmailAndPassword(this.auth, request.email, request.password)
    ).pipe(
      catchError(error => {
        console.error('Firebase login error:', error);
        return throwError(() => error);
      }),
      switchMap((userCredential: UserCredential) => 
        from(userCredential.user.getIdToken()).pipe(
          catchError(error => {
            console.error('Error getting token:', error);
            return throwError(() => error);
          }),
          switchMap((firebaseToken: string) => 
            this.http.post<AuthResponse>(`${this.publicUrl}/session/login`, {
              ...request,
              firebaseToken
            }).pipe(
              // Asegurar que la respuesta incluya el firebaseToken
              tap(resp => {
                if (!resp.token) {
                  resp.token = firebaseToken;
                }
              }),
              catchError(error => {
                console.error('API login error:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }

  /**
   * Login with Microsoft account using Firebase Authentication
   */
  private loginWithMicrosoftApi(): Observable<AuthResponse> {
    console.log('Attempting login with Microsoft');
    const provider = new OAuthProvider('microsoft.com');
    
    // Configure provider using environment values
    if (environment.firebaseProvider) {
      const { name, prompt, tenant, authority, scope } = environment.firebaseProvider;
      console.log('Provider:', name);
      
      provider.setCustomParameters({
        prompt: prompt,
        tenant: tenant,
        authority: authority
      });
      
      if (scope) {
        provider.addScope(scope);
      }
    }
    
    return from(
      signInWithPopup(this.auth, provider)
    ).pipe(
      catchError(error => {
        console.error('Firebase Microsoft login error:', error);
        return throwError(() => error);
      }),
      switchMap((userCredential: UserCredential) => 
        from(userCredential.user.getIdToken()).pipe(
          catchError(error => {
            console.error('Error getting token:', error);
            return throwError(() => error);
          }),
          switchMap((firebaseToken: string) => 
            this.http.post<AuthResponse>(`${this.publicUrl}/session/login`, {
              email: userCredential.user.email,
              firebaseToken,
              productCode: environment.defaultProductCode
            }).pipe(
              // Asegurar que la respuesta incluya el firebaseToken
              tap(resp => {
                if (!resp.token) {
                  resp.token = firebaseToken;
                }
              }),
              catchError(error => {
                console.error('API login error:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }

  /**
   * Send password reset email using Firebase Authentication
   */
  private forgotPasswordApi(email: string): Observable<void> {
    console.log('Sending password reset email to:', email);
    return from(sendPasswordResetEmail(this.auth, email)).pipe(
      catchError(error => {
        console.error('Error sending reset email:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout user
   */
  private logoutApi(): Observable<void> {
    console.log('Logging out');
    
    // Primero limpiar cookies del backend, luego logout de Firebase
    return this.http.post(`${this.publicUrl}/logout`, {}).pipe(
      switchMap(() => from(this.auth.signOut())),
      catchError(error => {
        console.error('Error logging out:', error);
        // Intentar logout de Firebase aunque falle el backend
        return from(this.auth.signOut());
      })
    );
  }

  /**
   * Handle successful authentication
   */
  private handleAuthSuccess(response: AuthResponse): void {
    console.log('Login successful');
    
    // Solo guardar auth_user en localStorage (auth_token va en cookies)
    localStorage.setItem('auth_user', JSON.stringify(response.account));
    
    // Actualizar el estado
    this.state.update(state => ({
      ...state,
      isAuthenticated: true,
      token: 'cookie-based', // Indicar que el token está en cookies
      account: response.account,
      loading: false,
      error: null
    }));
    
    // Mostrar mensaje de éxito
    this.snackBar.open('Inicio de sesión exitoso', 'Cerrar', { duration: 3000 });
    
    // Redirigir al dashboard
    setTimeout(() => {
      this.router.navigate(['/dashboard']);
    }, 100);
  }

  /**
   * Handle authentication error
   */
  private handleAuthError(error: any): void {
    console.error('Auth error:', error);
    
    // Generar mensaje de error
    let errorMessage = 'Error de autenticación';
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      errorMessage = 'Credenciales inválidas';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Demasiados intentos fallidos. Intente más tarde';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Error de conexión. Por favor verifique su internet';
    } else if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'Se canceló el inicio de sesión';
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      errorMessage = 'La cuenta ya existe con otro proveedor de autenticación';
    } else if (error.message) {
      errorMessage += ' - ' + error.message;
    }
    
    // Actualizar estado
    this.state.update(state => ({
      ...state,
      loading: false,
      error: errorMessage
    }));
    
    this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000 });
  }

  /**
   * Limpiar datos de autenticación
   */
  private clearAuthData(): void {
    // Solo limpiar auth_user de localStorage (auth_token se limpia por cookies)
    localStorage.removeItem('auth_user');
    
    // Actualizar estado
    this.state.update(state => ({
      ...state,
      isAuthenticated: false,
      account: null,
      token: null,
      loading: false,
      error: null
    }));
    
    console.log('Auth data cleared successfully');
  }

  /**
   * Validate token and check permissions
   */
  validateToken(request: TokenValidationRequest): Observable<TokenValidationResponse> {
    return this.http.post<TokenValidationResponse>(`${this.baseUrl}/validate`, request).pipe(
      catchError(error => {
        console.error('Error validating token:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if user has specific permission
   */
  hasPermissionFor(permissionId: string): boolean {
    const user = this.account();
    if (user && user.permissions) {
      return user.permissions.some((p: string) => p === permissionId);
    }
    return false;
  }

  /**
   * Check if user can access feature
   */
  canAccessFeature(modulePreffix: string): boolean {
    const user = this.account();
    if (user && user.permissions) {
      return user.permissions.some((p: string) => p.startsWith(modulePreffix));
    }
    return false;
  }
}